import { createSelector } from 'reselect';
import { formValueSelector } from 'redux-form';
import without from 'lodash/without';

import {
  TYRO_TERMINAL,
  CASH_DRAWER,
  REGISTER,
  RECEIPT_PRINTER,
  SPI_TERMINAL,
  WINDCAVE_TERMINAL,
  FREEDOMPAY_TERMINAL,
  AURUS_TERMINAL,
  SERVICE_PRINTER,
} from '../../../../constants/constant_device_types';
import { DeviceForm } from '../../../../constants/constant_form_names';

const selector = formValueSelector(DeviceForm);

function getFormId(state) {
  const { _id, id } = selector(state, '_id', 'id');
  return _id || id;
}

function getFormDeviceId(state) {
  return selector(state, 'device_id');
}

function getFormDeviceName(state) {
  return selector(state, 'name');
}

function getFormLocationId(state) {
  return selector(state, 'location_id') || null;
}

function getFormSignatureId(state) {
  return selector(state, 'signature_id');
}

function getFormPrinterBrand(state) {
  return selector(state, 'printer_id');
}

function getFormESKey(state) {
  return selector(state, 'pairing_config.es_key');
}

function getFormDeviceTypeCid(state) {
  return selector(state, 'device_type.cid') || {};
}

// function getFormDeviceType(state) {
//   return selector(state, "device_type_id") || {};
// }

function getFormIntegrationKey(state) {
  return selector(state, 'integration_key');
}

function getFormStatusList(state) {
  return state.lists.device_status;
}

function getFormMerchantId(state) {
  return selector(state, 'merchant_id');
}

function getFormTerminalId(state) {
  return selector(state, 'terminal_id');
}

function getFormAttachedDevices(state) {
  return selector(state, 'attached_devices') || [];
}

function getFormAttachedDeviceByIndex(state, index) {
  return selector(state, `attached_devices[${index}]`) || {};
}

function getFormEmployees(state) {
  return selector(state, 'employees') || [];
}

function getFormEmployeeByIndex(state, index) {
  return selector(state, `employees[${index}]`) || {};
}

function getFormViewMode(state, viewMode) {
  return viewMode;
}

export const isTyroTerminal = createSelector(
  [getFormDeviceTypeCid],
  (deviceTypeCid) => {
    return deviceTypeCid === TYRO_TERMINAL;
  }
);

export const isCashDrawer = createSelector(
  [getFormDeviceTypeCid],
  (deviceTypeCid) => {
    return deviceTypeCid === CASH_DRAWER;
  }
);

export const isRegister = createSelector(
  [getFormDeviceTypeCid],
  (deviceTypeCid) => {
    return deviceTypeCid === REGISTER;
  }
);

export const isSPITerminal = createSelector(
  [getFormDeviceTypeCid],
  (deviceTypeCid) => {
    return deviceTypeCid === SPI_TERMINAL;
  }
);

export const isWindcaveTerminal = createSelector(
  [getFormDeviceTypeCid],
  (deviceTypeCid) => {
    return deviceTypeCid === WINDCAVE_TERMINAL;
  }
);

export const isFreedomPayTerminal = createSelector(
  [getFormDeviceTypeCid],
  (deviceTypeCid) => {
    return deviceTypeCid === FREEDOMPAY_TERMINAL;
  }
);

export const isAurusTerminal = createSelector(
  [getFormDeviceTypeCid],
  (deviceTypeCid) => {
    return deviceTypeCid === AURUS_TERMINAL;
  }
);

export const isReceiptPrinter = createSelector(
  [getFormDeviceTypeCid],
  (deviceTypeCid) => deviceTypeCid === RECEIPT_PRINTER
);

export const isPrinter = createSelector(
  [getFormDeviceTypeCid],
  (deviceTypeCid) => [RECEIPT_PRINTER, SERVICE_PRINTER].includes(deviceTypeCid)
);

export const hasIntegrationKey = createSelector(
  [getFormIntegrationKey],
  (integrationKey) => {
    return Boolean(integrationKey);
  }
);

export const getStatusList = createSelector(
  [getFormStatusList],
  (statusList) => {
    return statusList;
  }
);

export const getTerminalId = createSelector(
  [getFormTerminalId],
  (terminalId) => {
    return terminalId;
  }
);

export const getMerchantId = createSelector(
  [getFormMerchantId],
  (merchantId) => {
    return merchantId;
  }
);

// export const getLocation = createSelector([getFormLocationId], location => {
//   return location;
// });

export const getLocationId = createSelector(
  [getFormLocationId],
  (locationId) => {
    return locationId;
  }
);

export const getFilterDeviceIds = createSelector(
  [getFormAttachedDevices, getFormAttachedDeviceByIndex],
  (attachedDevices, ownAttachedDevice) => {
    const attachedDeviceIds = attachedDevices.map(
      (attachedDevice) => attachedDevice.device_id
    );
    const ownDeviceId = ownAttachedDevice.device_id;
    const selectedDeviceIds = without(
      attachedDeviceIds,
      null,
      undefined,
      '',
      ownDeviceId
    );
    return selectedDeviceIds;
  }
);

export const isAttachedDeviceDefaultPrinter = createSelector(
  [getFormAttachedDeviceByIndex],
  (attachedDevice) => {
    return attachedDevice.default;
  }
);

export const isAttachedDeviceReceiptPrinter = createSelector(
  [getFormAttachedDeviceByIndex],
  (attachedDevice) => {
    const deviceType = attachedDevice.device_type;
    return (deviceType || {}).cid === RECEIPT_PRINTER;
  }
);

export const getAttachedDeviceOnEditMode = createSelector(
  [getFormAttachedDeviceByIndex],
  (attachedDevice) => {
    return attachedDevice.onEditMode;
  }
);

export const getAttachedDeviceDeviceType = createSelector(
  [getFormAttachedDeviceByIndex],
  (attachedDevice) => {
    if ((attachedDevice.device_type || {}).cid) {
      return (attachedDevice.device_type || {}).cid || '';
    }

    return attachedDevice.device_type_id || '';
  }
);

export const hasAttachedReceiptPrinter = createSelector(
  [getFormAttachedDevices],
  (attachedDevices) => {
    return attachedDevices.some(
      (attachedDevice) =>
        (attachedDevice.device_type || {}).cid === RECEIPT_PRINTER
    );
  }
);

export const getEditModeAttachedDeviceIndex = createSelector(
  [getFormAttachedDevices],
  (attachedDevices) => {
    if (!attachedDevices.length) return null;
    return attachedDevices.findIndex((attachedDevice) => {
      return attachedDevice && attachedDevice.onEditMode;
    });
  }
);

export const getUnfilledAttachedDeviceIndex = createSelector(
  [getFormAttachedDevices],
  (attachedDevices) => {
    return attachedDevices.findIndex((attachedDevice) => {
      return attachedDevice && !attachedDevice.device_id;
    });
  }
);

export const getEmployeeOnEditMode = createSelector(
  [getFormEmployeeByIndex],
  (employee) => {
    return employee.onEditMode;
  }
);

export const getEditModeEmployeeIndex = createSelector(
  [getFormEmployees],
  (employees) => {
    if (!employees.length) return null;
    return employees.findIndex((employee) => {
      return employee && employee.onEditMode;
    });
  }
);

export const getUnfilledEmployeeIndex = createSelector(
  [getFormEmployees],
  (employees) => {
    return employees.findIndex((employee) => {
      return employee && !employee.employee_id;
    });
  }
);

export const isEmployeeActive = createSelector(
  [getFormEmployeeByIndex],
  (employee) => {
    return employee.active;
  }
);

export const getFilterEmployeeIds = createSelector(
  [getFormEmployees, getFormEmployeeByIndex],
  (employees, ownEmployee) => {
    const employeeIds = employees.map((employee) => employee.employee_id);
    const ownEmployeeId = ownEmployee.employee_id;
    const selectedEmployeeIds = without(
      employeeIds,
      null,
      undefined,
      '',
      ownEmployeeId
    );
    return selectedEmployeeIds;
  }
);

export const showResetButton = createSelector(
  [getFormSignatureId, isRegister, getFormViewMode],
  (signatureId, isRegister, viewMode) => {
    return Boolean(signatureId) && isRegister && !viewMode;
  }
);

export const isExistingDevice = createSelector([getFormId], (id) => {
  return Boolean(id);
});

export const getHeader = createSelector(
  [getFormDeviceName, getFormDeviceId],
  (name, deviceId) => {
    return `${name} - ${deviceId}`;
  }
);

export const getName = createSelector([getFormDeviceName], (name) => {
  return name;
});


export const getESKey = createSelector(
  [getFormESKey],
  (esKey) => {
    return esKey || '';
  }
);

export const isEpsonPrinter = createSelector(
  [getFormPrinterBrand],
  (printerBrand) => {
    return printerBrand === 'epson';
  }
);